import { queryClient } from '@/lib/queryClient';
import { markMutationQueriesStale } from './markMutationQueriesStale';
// Always prefer explicit env configuration; fallback to /api for local dev.
import { clearStoredAuthSession } from '@/lib/auth/session';
import { isDemoVariant } from '@/demo/config';
import { buildAuthHeaders, resolveRequestFundId } from './apiAuthHeaders';
import { buildNetworkError } from './networkError';
import { isPageLeaving } from '@/lib/pageLifecycle';
import { openAuthenticatedResponse } from './openAuthenticatedResponse';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { noticeHeldSend } from './demoCopyHeld';
import { refreshSession } from '@/lib/auth/refreshSession';
import {
  ApiRequestError,
  apiRequestErrorFromBody,
  readApiErrorMessage,
  type ApiErrorBody,
} from './apiRequestError';

export { buildAuthHeaders, resolveRequestFundId } from './apiAuthHeaders';
export { ApiNetworkError } from './networkError';
export { ApiRequestError } from './apiRequestError';

// Public production requests stay on the frontend origin so session cookies
// are first-party. Vercel proxies /api to the Railway service.
const API_URL = resolveApiBaseUrl({
  configuredApiUrl: import.meta.env.VITE_API_URL,
  hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
  pathname: typeof window === 'undefined' ? undefined : window.location.pathname,
});

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

function parseJsonResponseBody(text: string, response: Response, endpoint: string): unknown {
  const contentType = response.headers.get('content-type') ?? '';
  const looksLikeJson = contentType.includes('application/json') || contentType.includes('+json');
  if (!looksLikeJson) {
    throw new ApiRequestError(
      response.ok
        ? `API returned a non-JSON response for ${endpoint} — the request likely never reached the API server (check /api rewrites and VITE_API_URL).`
        : `API request failed (${response.status}) for ${endpoint}.`,
      response.status,
      { endpoint, contentType, bodyPreview: text.slice(0, 200) },
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiRequestError(
      `API returned malformed JSON for ${endpoint}.`,
      response.status,
      { endpoint, contentType, bodyPreview: text.slice(0, 200) },
    );
  }
}

function isCsrfFailure(
  status: number,
  data: ApiErrorBody | Record<string, unknown>,
): boolean {
  if (status !== 403) return false;
  return readApiErrorMessage(data).includes('Invalid or missing CSRF token');
}

// Track if a token refresh is in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
const NETWORK_RETRY_DELAY_MS = 350;
const CSRF_STORAGE_KEY = 'ls_csrf_token_cache';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getCsrfToken(): string | null {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|;\s*)ls_csrf_token=([^;]+)/);
    if (match?.[1]) {
      try {
        const token = decodeURIComponent(match[1]);
        localStorage.setItem(CSRF_STORAGE_KEY, token);
        return token;
      } catch {
        localStorage.setItem(CSRF_STORAGE_KEY, match[1]);
        return match[1];
      }
    }
  }
  if (typeof localStorage === 'undefined') {
    return null;
  }

  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

function syncCsrfTokenFromResponse(response: Response): void {
  const token = response.headers.get('x-csrf-token');
  if (!token) return;

  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // Ignore storage write failures; in-memory request flow can still proceed.
  }
}

async function executeApiFetch(
  endpoint: string,
  requestInit: RequestInit,
  retryAttempts: number,
): Promise<Response> {
  const url = `${API_URL}${endpoint}`;
  try {
    return noticeHeldSend(requestInit.method === 'GET'
      ? await fetchWithRetry(url, requestInit, retryAttempts)
      : await fetch(url, requestInit));
  } catch (error) {
    if (requestInit.signal?.aborted || isPageLeaving()) throw error;
    const networkError = buildNetworkError(endpoint, url, requestInit, retryAttempts, error);
    console.error('[api] network request failed', networkError.details);
    throw networkError;
  }
}

async function fetchWithRetry(url: string, options: RequestInit, maxAttempts = 2): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fetch(url, {
        credentials: options.credentials ?? 'include',
        cache: options.cache ?? 'no-store',
        ...options,
      });
    } catch (error) {
      lastError = error;
      if (options.signal?.aborted) throw error;
      if (attempt >= maxAttempts) break;
      await sleep(NETWORK_RETRY_DELAY_MS * attempt);
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Network request failed');
}

/** Opens a response body for incremental consumers while preserving auth and CSRF recovery. */
export async function apiOpenResponse(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<Response> {
  if (import.meta.env.VITE_APP_VARIANT && isDemoVariant) {
    const { resolveDemoApiRequest } = await import('@/demo/mockApi');
    const demoResponse = await resolveDemoApiRequest<unknown[]>(endpoint, options);
    if (demoResponse.handled) {
      return new Response(demoResponse.data.map((item) => JSON.stringify(item)).join('\n'), {
        status: 200,
        headers: { 'Content-Type': 'application/x-ndjson' },
      });
    }
  }
  return openAuthenticatedResponse({
    buildHeaders: buildAuthHeaders, addCsrfHeader,
    execute: (path, init) => executeApiFetch(path, init, 1),
    syncCsrf: syncCsrfTokenFromResponse, refresh: handleTokenRefresh,
    clearSession: clearStoredAuthSession, rehydrateCsrf: rehydrateCsrfToken,
    isCsrfFailure,
    makeError: apiRequestErrorFromBody,
  }, endpoint, options, isRetry);
}

function addCsrfHeader(method: string, headers: Record<string, string>): Record<string, string> {
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') {
    return headers;
  }

  const csrfToken = getCsrfToken();
  if (!csrfToken || headers['x-csrf-token']) {
    return headers;
  }

  return {
    ...headers,
    'x-csrf-token': csrfToken,
  };
}

async function rehydrateCsrfToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: buildAuthHeaders(false),
    });

    syncCsrfTokenFromResponse(response);
    return response.ok;
  } catch {
    return false;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  return refreshSession({
    apiUrl: API_URL, syncCsrf: syncCsrfTokenFromResponse,
    headers: () => buildAuthHeaders(false),
  });
}

async function handleTokenRefresh(): Promise<boolean> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> {
  if (import.meta.env.VITE_APP_VARIANT && isDemoVariant) {
    const { resolveDemoApiRequest } = await import('@/demo/mockApi');
    const demoResponse = await resolveDemoApiRequest<T>(endpoint, options);
    if (demoResponse.handled) {
      await markMutationQueriesStale(queryClient, endpoint, (options.method ?? 'GET').toUpperCase(), resolveRequestFundId());
      return demoResponse.data;
    }
  }

  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = addCsrfHeader(method, buildAuthHeaders(
    hasBody && !isFormData,
    (options.headers as Record<string, string> | undefined) ?? {}
  ));

  const requestInit = {
    credentials: options.credentials ?? 'include',
    ...options,
    headers,
  };
  const response = await executeApiFetch(endpoint, requestInit, method === 'GET' ? 2 : 1);
  syncCsrfTokenFromResponse(response);

  // Handle 401 Unauthorized - attempt token refresh and retry
  if (response.status === 401 && !isRetry && !endpoint.includes('/auth/refresh') && !endpoint.includes('/auth/logout')) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      // Retry the original request with the new token
      return apiRequest<T>(endpoint, options, true);
    }
    
    // Refresh failed - clear tokens and let the error propagate
    clearStoredAuthSession();
    const { useAuthStore } = await import('@/stores/authStore');
    useAuthStore.getState().clearAuth();
    throw new ApiRequestError('Unauthorized', 401);
  }

  const text = await response.text();
  if (!text) {
    if (!response.ok) {
      throw new ApiRequestError('API request failed', response.status);
    }
    await markMutationQueriesStale(queryClient, endpoint, method, headers['x-fund-id'] ?? null);
    return undefined as T;
  }

  const data = parseJsonResponseBody(text, response, endpoint) as ApiResponse<T> | ApiErrorBody;

  if (
    isCsrfFailure(response.status, data as unknown as Record<string, unknown>) &&
    !isRetry &&
    !endpoint.includes('/auth/refresh') &&
    !endpoint.includes('/auth/logout')
  ) {
    const csrfRecovered = await rehydrateCsrfToken();
    if (csrfRecovered) {
      return apiRequest<T>(endpoint, options, true);
    }

    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      return apiRequest<T>(endpoint, options, true);
    }
  }

  if (!response.ok || !('success' in data) || !data.success) {
    throw apiRequestErrorFromBody(data, response.status);
  }

  await markMutationQueriesStale(queryClient, endpoint, method, headers['x-fund-id'] ?? null);
  return (data as ApiResponse<T>).data as T;
}

async function apiRequestRaw<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> {
  if (import.meta.env.VITE_APP_VARIANT && isDemoVariant) {
    const { resolveDemoApiRequest } = await import('@/demo/mockApi');
    const demoResponse = await resolveDemoApiRequest<T>(endpoint, {
      ...options,
      raw: true,
    });
    if (demoResponse.handled) {
      await markMutationQueriesStale(queryClient, endpoint, (options.method ?? 'GET').toUpperCase(), resolveRequestFundId());
      return demoResponse.data;
    }
  }

  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = addCsrfHeader(method, buildAuthHeaders(
    hasBody && !isFormData,
    (options.headers as Record<string, string> | undefined) ?? {}
  ));

  const requestInit = {
    credentials: options.credentials ?? 'include',
    ...options,
    headers,
  };
  const response = await executeApiFetch(endpoint, requestInit, method === 'GET' ? 2 : 1);
  syncCsrfTokenFromResponse(response);

  if (response.status === 401 && !isRetry && !endpoint.includes('/auth/refresh') && !endpoint.includes('/auth/logout')) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      return apiRequestRaw<T>(endpoint, options, true);
    }

    clearStoredAuthSession();
    const { useAuthStore } = await import('@/stores/authStore');
    useAuthStore.getState().clearAuth();
    throw new ApiRequestError('Unauthorized', 401);
  }

  const text = await response.text();
  if (!text) {
    if (!response.ok) {
      throw new ApiRequestError('API request failed', response.status);
    }
    await markMutationQueriesStale(queryClient, endpoint, method, headers['x-fund-id'] ?? null);
    return undefined as T;
  }

  const data = parseJsonResponseBody(text, response, endpoint) as Record<string, unknown>;

  if (
    isCsrfFailure(response.status, data) &&
    !isRetry &&
    !endpoint.includes('/auth/refresh') &&
    !endpoint.includes('/auth/logout')
  ) {
    const csrfRecovered = await rehydrateCsrfToken();
    if (csrfRecovered) {
      return apiRequestRaw<T>(endpoint, options, true);
    }

    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      return apiRequestRaw<T>(endpoint, options, true);
    }
  }

  if (!response.ok) {
    throw apiRequestErrorFromBody(data, response.status);
  }

  await markMutationQueriesStale(queryClient, endpoint, method, headers['x-fund-id'] ?? null);
  return data as T;
}

export interface ApiBlobResponse {
  blob: Blob;
  headers: Headers;
}

export async function apiRequestBlobResponse(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<ApiBlobResponse> {
  if (import.meta.env.VITE_APP_VARIANT && isDemoVariant) {
    const { resolveDemoApiRequest } = await import('@/demo/mockApi');
    const demoResponse = await resolveDemoApiRequest<ApiBlobResponse>(endpoint, {
      ...options,
      raw: true,
    });
    if (demoResponse.handled) {
      await markMutationQueriesStale(queryClient, endpoint, (options.method ?? 'GET').toUpperCase(), resolveRequestFundId());
      return demoResponse.data;
    }
  }

  const method = (options.method ?? 'GET').toUpperCase();
  const requestInit: RequestInit = {
    credentials: options.credentials ?? 'include',
    cache: 'no-store',
    ...options,
    headers: addCsrfHeader(method, buildAuthHeaders(
      false, (options.headers as Record<string, string> | undefined) ?? {},
    )),
  };
  const response = await executeApiFetch(endpoint, requestInit, method === 'GET' ? 2 : 1);
  if (response.status === 401 && !isRetry) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) return apiRequestBlobResponse(endpoint, options, true);
    clearStoredAuthSession();
    throw new ApiRequestError('Unauthorized', 401);
  }
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw apiRequestErrorFromBody(data, response.status);
  }
  return { blob: await response.blob(), headers: response.headers };
}

export const api = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  patch: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: <T>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
  raw: {
    request: <T>(endpoint: string, options: RequestInit = {}) =>
      apiRequestRaw<T>(endpoint, options),
    get: <T>(endpoint: string, options?: RequestInit) =>
      apiRequestRaw<T>(endpoint, { ...options, method: 'GET' }),
    post: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
      apiRequestRaw<T>(endpoint, {
        ...options,
        method: 'POST',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    patch: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
      apiRequestRaw<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: <T>(endpoint: string, options?: RequestInit) =>
      apiRequestRaw<T>(endpoint, { ...options, method: 'DELETE' }),
  },
};
